import * as angular from 'angular';
import * as _ from "lodash";
import {apsEnums} from "../../shared/dto/aps.enums";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import {StateParams} from "../../common";
import {apsEvents} from "../../shared/dto/aps.events";
import {Strings} from "../../shared/config/constant.module";
import DateUtilityService from "../../services/utility/dateutility.service";
import CheckListInstancesServiceName, {CheckListInstancesService} from "../../services/checkLists/checkListInstances.service";
import AuthDataServiceName, {AuthDataService} from "../../services/auth/authData.service";
import CheckListInstancesHubName, {CheckListInstancesHub, CheckListInstancesHubCreator} from "../../services/checkLists/checkListInstances.hub";
import ShopServiceName, {ShopService} from "../../services/shops/shops.service";
import FillOutCheckListName, {openFillOutCheckList} from "../checks/fillOutCheckList.controller";
let requires = [CheckListInstancesServiceName, AuthDataServiceName, CheckListInstancesHubName, ShopServiceName,
  FillOutCheckListName];

interface ScheduledCheckListsController {
  list: CheckListItem[];
  checkListView: apsEnums.CheckListView;
  loading: boolean;
  formatTime(time): string;
  open(checkListInstance: CheckListItem): void;
  viewAllChanged(viewAll: boolean): void;
}

interface CheckListItem extends apsViewModels.CheckListInstanceListItem {
  plantId: string;
  shopId: string;
  isPending: boolean;
  isNotPending: boolean;
  isNotScheduled: boolean;
  canOpen(): boolean;
}

class ScheduledCheckListsCtrl implements ScheduledCheckListsController {
  list: CheckListItem[];
  checkListView: apsEnums.CheckListView;
  loading: boolean;
  private plantId: string;
  private shopId: string;
  private viewAll: boolean;
  private shop: apsViewModels.ShopConfiguration;
  private timeZoneId: string;
  private $modal: mm.foundation.modal;
  private shopPromise: angular.IPromise<any>;
  private $q: angular.IQService;
  private checkListInstancesService: CheckListInstancesService;
  private authDataService: AuthDataService;
  private hub: CheckListInstancesHub;
  private $scope: angular.IScope;
  private $timeout: angular.ITimeoutService;

  constructor(
    checkListInstancesService: CheckListInstancesService,
    $modal: mm.foundation.modal,
    $q: angular.IQService,
    authDataService: AuthDataService,
    shopService: ShopService,
    checkListInstancesHubCreator: CheckListInstancesHubCreator,
    $scope: angular.IScope,
    $timeout: angular.ITimeoutService,
    $stateParams: StateParams) {
    this.plantId = $stateParams.plantId;
    this.shopId = $stateParams.shopId;
    this.timeZoneId = Strings.DEFAULT_TIMEZONE;
    this.$modal = $modal;
    this.checkListInstancesService = checkListInstancesService;
    this.authDataService = authDataService;
    this.loading = true;
    this.$q = $q;
    this.viewAll = false;
    this.$scope = $scope;
    this.$timeout = $timeout;

    this.shopPromise = shopService
      .getShopConfiguration(this.shopId)
      .then((result) => {
        this.shop = result;
        this.timeZoneId = result.timeZoneId;

        this.hub = checkListInstancesHubCreator.createCheckListInstancesHub(
          this.plantId, this.shopId,
          (updated) => this.handleUpdated(updated),
          (instance) => this.handleInstance(instance));
      }
    );

    this.$scope.$on('$destroy', () => {
      if (this.hub) {
        this.hub.uninitialize();
      }
    });
  }

  private handleUpdated(updated: apsEvents.CheckListInstancesUpdated) {
    if (!this.list) {
      return;
    }

    if (updated.instances.length > 0) {
      _.forEach(updated.instances, i => this.handleUpdated(i));
    }

    var existing = _.find(this.list, {id: updated.id});
    if (!existing) {
      return;
    }

    this.$timeout(() => {
      if (updated.action === apsEnums.EventAction.Delete
        || updated.status >= apsEnums.CheckListStatus.Complete) {
        _.remove(this.list, existing);
      } else {
        existing.status = updated.status;
        existing.result = updated.result;
      }
    });
  }

  private handleNewInstance(instance: apsViewModels.CheckListInstanceItemEvent): boolean {
    if (instance.status >= apsEnums.CheckListStatus.Complete || instance.checkListIsGroup) {
      return false;
    }

    var currentRole = this.authDataService.getSelectedRoleLocation();
    var roleId = currentRole.role.roleId;
    if (instance.roleId !== roleId
      && !_.some(instance.allowableRoles, r => r === roleId)) {
      return false;
    }

    var newItem = this.transformCheckListInstance(instance);
    var index = _.findLastIndex(this.list, (i: CheckListItem) => i.scheduledOn) + 1;
    if (index > this.list.length) {
      this.list.push(newItem);
    } else {
      this.list.splice(index, 0, newItem);
    }
    return true;
  }

  private handleInstance(instance: apsViewModels.CheckListInstanceItemEvent) {
    if (!this.list) {
      return;
    }

    if (instance.instances.length > 0) {
      _.forEach(instance.instances, i => this.handleInstance(i));
    }

    this.$timeout(() => {
      if (instance.action === apsEnums.EventAction.Add) {
        if (!this.handleNewInstance(instance)) {
          return;
        }
      } else {
        var existing = _.find(this.list, {id: instance.id});
        if (!existing) {
          if (!this.handleNewInstance(instance)) {
            return;
          }
        } else if (instance.status >= apsEnums.CheckListStatus.Complete) {
          _.remove(this.list, existing);
        } else {
          existing.status = instance.status;
          existing.result = instance.result;
        }
      }
    });
  }

  private getData() {
    var currentRole = this.authDataService.getSelectedRoleLocation();
    var roleId = currentRole.role.roleId;
    return this.checkListInstancesService
      .getByRole(this.plantId, this.shopId, roleId, this.checkListView, this.viewAll)
      .then((data) => {
        var filteredInstances = _.filter(data, (instance) => this.checkListInstancesService.isAuthorizedForCurrentPlant(instance));
        this.list = _.map(filteredInstances, (instance) => this.transformCheckListInstance(instance));
      });
  }

  viewAllChanged(viewAll: boolean): void {
    this.viewAll = viewAll;
    this.loading = true;
    this.$q.all([this.shopPromise, this.getData()]).then(() => this.loading = false);
  }

  private transformCheckListInstance(instance: apsViewModels.CheckListInstanceListItem): CheckListItem {
    var checkListItem = <CheckListItem>_.assign({}, instance);
    checkListItem.isPending = instance.isScheduled && instance.status === apsEnums.CheckListStatus.Pending;
    checkListItem.isNotPending = instance.isScheduled && instance.status !== apsEnums.CheckListStatus.Pending;
    checkListItem.isNotScheduled = !instance.isScheduled;
    checkListItem.canOpen = () => {
      return checkListItem.isNotScheduled || checkListItem.isNotPending;
    };
    return checkListItem;
  }

  formatTime(time) {
    return DateUtilityService.formatTime(time, this.timeZoneId);
  }

  open(checkListInstance: CheckListItem) {
    var result = openFillOutCheckList({
      checkListId: checkListInstance.checkListId,
      instanceId: checkListInstance.id,
      plantId: checkListInstance.plantId,
      tankId: checkListInstance.tankId,
      shopId: checkListInstance.shopId,
      plantLocationId: checkListInstance.plantLocationId,
      readOnly: false,
      scheduledOn: checkListInstance.scheduledOn
    }, this.$modal);

    // TODO: to handle new unscheduled check list instances,
    //       we would need to generate the id client side
    if (!checkListInstance.scheduledOn) {
      return;
    }

    result.then((cmd) => {
      var data = {
        status: checkListInstance.status,
        result: apsEnums.CheckListResult.Incomplete
      };
      this.checkListInstancesService.updateStatusAndResult(data, cmd);
      if (data.status === apsEnums.CheckListStatus.Complete) {
        // TODO: instead of removing, we could change the instance to upcoming
        // it would require updating the scheduled and expires dates.
        // It would also require moving it below other scheduled instances.
        // For now, we can rely that the event to update it will eventually come through.
        _.remove(this.list, checkListInstance);
      }
    });
  }
}

export default angular.module('aps.controller.scheduledCheckLists', requires)
  .controller('ScheduledCheckListsCtrl', ScheduledCheckListsCtrl)
  .name;



// WEBPACK FOOTER //
// ./src/app/controllers/checkLists/scheduledCheckLists.controller.ts